import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { removeBundleFromCompany } from '../../services/selfServeEntitlements';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const SelfServeEntitlements = ({ subscription, companyId, entitlementDescriptions, onRefresh }) => {
  const [expandedBundles, setExpandedBundles] = useState({});
  const [removingBundleId, setRemovingBundleId] = useState(null);
  if (!subscription) {
    return <Typography>No subscription data available.</Typography>;
  }

  const handleToggle = (id) => {
    setExpandedBundles((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRemove = async (bundleId) => {
    if (!window.confirm('Are you sure you want to remove this bundle?')) return;
    setRemovingBundleId(bundleId);

    try {
      await removeBundleFromCompany(companyId, bundleId);
      onRefresh?.(); // Refresh data
    } catch (err) {
      console.error('Error removing bundle:', err);
    } finally {
      setRemovingBundleId(null);
    }
  };

  const { entitlements = {}, basedOn = [] } = subscription;

  return (
    <Box sx={{ p: 3 }}>
      {/* Section: Entitlements */}
      <Typography variant="h6" gutterBottom>
        Entitlements
      </Typography>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {Object.entries(entitlements).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box
                  sx={{
                    border: '1px solid #eee',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#fafafa',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {key}
                    </Typography>
                    {entitlementDescriptions?.[key]?.description && (
                      <Tooltip title={entitlementDescriptions[key].description} arrow>
                        <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography variant="body1">
                    {typeof value === 'boolean'
                      ? value
                        ? 'Enabled'
                        : 'Disabled'
                      : value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Section: Bundles */}
      <Typography variant="h6" gutterBottom>
        Bundles
      </Typography>

      {basedOn.length === 0 ? (
        <Typography color="text.secondary">No bundles available.</Typography>
      ) : (
        basedOn.map((bundle) => (
          <Card
            key={bundle._id}
            variant="outlined"
            sx={{
              mb: 2,
              position: 'relative',
              p: 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ pb: 0 }}>
              {/* Header Row */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {bundle.title.replace(/^GSD:/, '').trim()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Product ID: {bundle.productId}
                  </Typography>
                </Box>

                {/* Remove button aligned top right */}
                <IconButton
                  aria-label="remove bundle"
                  color="error"
                  onClick={() => handleRemove(bundle._id)}
                  disabled={removingBundleId === bundle._id}
                  sx={{
                    ml: 2,
                    '&:hover': { backgroundColor: 'rgba(255,0,0,0.08)' },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </CardContent>

            {/* Actions */}
            <CardActions
              sx={{
                justifyContent: 'flex-end',
                pt: 0,
                pr: 2,
              }}
            >
              <Button
                size="small"
                endIcon={
                  expandedBundles[bundle._id] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
                onClick={() => handleToggle(bundle._id)}
              >
                {expandedBundles[bundle._id] ? 'Hide Details' : 'View Details'}
              </Button>
            </CardActions>

            {/* Expandable Section */}
            <Collapse
              in={expandedBundles[bundle._id]}
              timeout="auto"
              unmountOnExit
            >
              <Divider />
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Entitlements in this bundle:
                </Typography>
                <List dense>

                  {bundle.entitlements.map((ent) => (
                    <ListItem key={ent.entitlement} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body1">{ent.entitlement}</Typography>
                            {entitlementDescriptions?.[ent.entitlement]?.description && (
                              <Tooltip title={entitlementDescriptions[ent.entitlement].description} arrow>
                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          ent.type === "feature"
                            ? `enable: ${ent.enabled}`
                            : `Quantity: ${ent.allocatedQuantity}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Collapse>
          </Card>
        ))
      )}
    </Box>
  );
};

export default SelfServeEntitlements;
